import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Container,
  Card,
  Modal,
  Badge,
  ListGroup,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ShimmerTable } from "../common/ShimmerTable";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { isMobile } from "react-device-detect";
import moment from "moment";
import {
  DatatableWrapper,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { Link } from "react-router-dom";
import ImportLeads from "./ImportLeads";
import {
  FaPlus,
  FaTags,
  FaUpload,
  FaTimes,
  FaSearch,
  FaFilter,
  FaTag,
  FaUsers,
  FaUserPlus,
  FaUserFriends,
  FaUserMinus,
  FaUserCheck,
  FaThumbtack,
  FaBan,
  FaDownload,
} from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import Select from "react-select";
import Confirm from "../Confirm";

// Context so select-all checkbox re-renders when selection changes (header array stays stable for pagination)
const LeadListSelectionContext = React.createContext({
  selectedRows: [],
  body: [],
  setSelectedRows: () => { },
  totalLeads: 0,
});

// Select all on this page only; merge with existing selection. Uncheck clears only this page's ids.
function SelectAllCheckbox() {
  const { selectedRows, body, setSelectedRows, totalLeads } = React.useContext(
    LeadListSelectionContext,
  );
  const pageIds = body.map((row) => row.id);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedRows.includes(id));
  return (
    <Form.Check
      type="checkbox"
      id="select-all-checkbox"
      checked={allOnPageSelected}
      onChange={(e) => {
        if (e.target.checked) {
          setSelectedRows((prev) => [...new Set([...prev, ...pageIds])]);
          const otherPages = Math.max(0, totalLeads - body.length);
          if (body.length > 0) {
            toast.info(
              `Selected all ${body.length} lead(s) on this page. Total matching your filters: ${totalLeads}. ${otherPages > 0 ? `${otherPages} lead(s) are on other pages — go to the next page to add them to your selection.` : ""}`,
              { autoClose: 6000 },
            );
          }
        } else {
          setSelectedRows((prev) => prev.filter((id) => !pageIds.includes(id)));
        }
      }}
    />
  );
}

const LeadList = ({ selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [leadname, setLeadName] = useState();
  const [leadStatusArray, setleadStatusArray] = useState(
    JSON.parse(sessionStorage.getItem("lead_status")),
  );
  const [isSpinner, setIsSpinner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState(null);
  const [pinLoading, setPinLoading] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalLeads, setTotalLeads] = useState(0);
  const [listBusy, setListBusy] = useState(false);
  const tableReadyRef = useRef(false);
  const leadCacheRef = useRef(new Map());
  /** In-memory cache: same key as API params → { records, total }. Avoids refetch when revisiting a page. */
  const pageListCacheRef = useRef(new Map());
  const LIST_PAGE_CACHE_MAX = 48;

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  // New state for assign agents modal
  const [showAssignAgentsModal, setShowAssignAgentsModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [assignMode, setAssignMode] = useState("replace"); // 'replace' or 'add'
  const [assignLoading, setAssignLoading] = useState(false);

  const [showBulkTagsModal, setShowBulkTagsModal] = useState(false);
  const [tagsForBulkApply, setTagsForBulkApply] = useState([]);
  const [tagBulkMode, setTagBulkMode] = useState("add");
  const [bulkTagsLoading, setBulkTagsLoading] = useState(false);
  const [downloadLeadsLoading, setDownloadLeadsLoading] = useState(false);
  const downloadLeadsInFlightRef = useRef(false);

  // New state variables for multi-tag search
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  /** Master tags from API (id + name) for bulk modal — filter sends tag ids so names can differ on leads. */
  const [tagMasterList, setTagMasterList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // State for agent filter
  const [selectedAgentFilter, setSelectedAgentFilter] = useState([]);

  /** Created date range (YYYY-MM-DD); inclusive on both ends when both set */
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");

  /** Applied filters — only these drive API + cache keys (draft above updates on typing without fetching). */
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedSelectedTags, setAppliedSelectedTags] = useState([]);
  const [appliedSelectedAgentFilter, setAppliedSelectedAgentFilter] = useState(
    [],
  );
  const [appliedCreatedFromDate, setAppliedCreatedFromDate] = useState("");
  const [appliedCreatedToDate, setAppliedCreatedToDate] = useState("");

  const buildListCacheKey = useCallback(() => {
    const tags = [...appliedSelectedTags.map((t) => t.value)]
      .sort()
      .join("\u0001");
    const agents = [...appliedSelectedAgentFilter.map((a) => String(a.value))]
      .sort()
      .join("\u0001");
    return [
      page,
      pageSize,
      appliedSearchQuery.trim(),
      appliedStatusFilter || "",
      tags,
      agents,
      (appliedCreatedFromDate || "").trim(),
      (appliedCreatedToDate || "").trim(),
      selectedWhatsAppSetting || "",
    ].join("|");
  }, [
    page,
    pageSize,
    appliedSearchQuery,
    appliedStatusFilter,
    appliedSelectedTags,
    appliedSelectedAgentFilter,
    appliedCreatedFromDate,
    appliedCreatedToDate,
    selectedWhatsAppSetting,
  ]);

  /** Stable string for applied filter + page size — cache clear & reset page when these change. */
  const listFilterKey = useMemo(() => {
    const tags = [...appliedSelectedTags.map((t) => t.value)]
      .sort()
      .join("\u0001");
    const agents = [...appliedSelectedAgentFilter.map((a) => String(a.value))]
      .sort()
      .join("\u0001");
    return `${appliedSearchQuery.trim()}|${appliedStatusFilter || ""}|${tags}|${agents}|${pageSize}|${(appliedCreatedFromDate || "").trim()}|${(appliedCreatedToDate || "").trim()}|${selectedWhatsAppSetting || ""}`;
  }, [
    appliedSearchQuery,
    appliedStatusFilter,
    appliedSelectedTags,
    appliedSelectedAgentFilter,
    pageSize,
    appliedCreatedFromDate,
    appliedCreatedToDate,
    selectedWhatsAppSetting,
  ]);

  /** True when draft filters differ from applied (Apply should run). */
  const filtersDirty = useMemo(() => {
    if (searchQuery.trim() !== appliedSearchQuery.trim()) return true;
    if ((statusFilter || "") !== (appliedStatusFilter || "")) return true;
    const draftTagStr = [...selectedTags.map((t) => t.value)]
      .sort()
      .join("\u0001");
    const appliedTagStr = [...appliedSelectedTags.map((t) => t.value)]
      .sort()
      .join("\u0001");
    if (draftTagStr !== appliedTagStr) return true;
    const draftAgentStr = [...selectedAgentFilter.map((a) => String(a.value))]
      .sort()
      .join("\u0001");
    const appliedAgentStr = [
      ...appliedSelectedAgentFilter.map((a) => String(a.value)),
    ]
      .sort()
      .join("\u0001");
    if (draftAgentStr !== appliedAgentStr) return true;
    if (
      (createdFromDate || "").trim() !==
      (appliedCreatedFromDate || "").trim()
    )
      return true;
    if (
      (createdToDate || "").trim() !== (appliedCreatedToDate || "").trim()
    )
      return true;
    return false;
  }, [
    searchQuery,
    statusFilter,
    selectedTags,
    selectedAgentFilter,
    appliedSearchQuery,
    appliedStatusFilter,
    appliedSelectedTags,
    appliedSelectedAgentFilter,
    createdFromDate,
    createdToDate,
    appliedCreatedFromDate,
    appliedCreatedToDate,
  ]);

  // Refs to keep header reference stable (prevents DatatableWrapper from resetting pagination on checkbox change)
  const selectedRowsRef = useRef(selectedRows);
  const bodyRef = useRef(body);
  const prevListFilterKeyRef = useRef(null);
  selectedRowsRef.current = selectedRows;
  bodyRef.current = body;

  const selectionContextValue = useMemo(
    () => ({ selectedRows, body, setSelectedRows, totalLeads }),
    [selectedRows, body, totalLeads],
  );

  const totalPages = Math.max(1, Math.ceil(totalLeads / pageSize) || 1);
  const rangeFrom = totalLeads === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, totalLeads);

  const paginationLabels = useMemo(
    () => ({
      beforeSelect: " ",
      afterSelect:
        totalLeads > 0
          ? ` · ${rangeFrom}–${rangeTo} of ${totalLeads} leads${selectedRows.length > 0
            ? ` · ${selectedRows.length} selected`
            : ""
          }`
          : "",
    }),
    [totalLeads, rangeFrom, rangeTo, selectedRows.length],
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  const loadLeadsFromServer = useCallback(
    async (options = {}) => {
      const forceRefresh = options.forceRefresh === true;
      if (forceRefresh) {
        pageListCacheRef.current.clear();
      }
      const cacheKey = buildListCacheKey();

      if (!forceRefresh) {
        const cached = pageListCacheRef.current.get(cacheKey);
        if (cached) {
          setBody(cached.records);
          setTotalLeads(cached.total);
          cached.records.forEach((row) => {
            leadCacheRef.current.set(row.id, row);
          });
          if (!tableReadyRef.current) {
            setIsSpinner(true);
            tableReadyRef.current = true;
          }
          return;
        }
      }

      try {
        if (tableReadyRef.current) {
          setListBusy(true);
        } else {
          setIsSpinner(false);
        }
        const params = {
          page,
          pageSize,
          q: appliedSearchQuery.trim() || undefined,
          status: appliedStatusFilter || undefined,
          business_number: selectedWhatsAppSetting || undefined,
          tags:
            appliedSelectedTags.length > 0
              ? appliedSelectedTags.map((t) => t.value).join(",")
              : undefined,
          agents:
            appliedSelectedAgentFilter.length > 0
              ? appliedSelectedAgentFilter.map((a) => String(a.value)).join(",")
              : undefined,
          createdFrom: appliedCreatedFromDate.trim() || undefined,
          createdTo: appliedCreatedToDate.trim() || undefined,
        };
        const leads = await WhatsAppAPI.fetchLead(params);

        if (leads && leads.success) {
          const rows = leads.records || [];
          const total = typeof leads.total === "number" ? leads.total : 0;
          setBody(rows);
          setTotalLeads(total);
          pageListCacheRef.current.set(cacheKey, { records: rows, total });
          while (pageListCacheRef.current.size > LIST_PAGE_CACHE_MAX) {
            const oldest = pageListCacheRef.current.keys().next().value;
            pageListCacheRef.current.delete(oldest);
          }
          rows.forEach((row) => {
            leadCacheRef.current.set(row.id, row);
          });
        } else {
          setBody([]);
          setTotalLeads(0);
          toast.error("Failed to load leads. Please try again.");
        }
      } catch (error) {
        console.error("Error loading leads:", error);
        setBody([]);
        setTotalLeads(0);
        toast.error("Error loading leads. Please refresh the page.");
      } finally {
        setListBusy(false);
        setIsSpinner(true);
        tableReadyRef.current = true;
      }
    },
    [
      buildListCacheKey,
      page,
      pageSize,
      appliedSearchQuery,
      appliedStatusFilter,
      appliedSelectedTags,
      appliedSelectedAgentFilter,
      appliedCreatedFromDate,
      appliedCreatedToDate,
    ],
  );

  useEffect(() => {
    pageListCacheRef.current.clear();
  }, [listFilterKey]);

  useEffect(() => {
    loadLeadsFromServer();
  }, [loadLeadsFromServer]);

  useLayoutEffect(() => {
    if (prevListFilterKeyRef.current === listFilterKey) {
      return;
    }
    prevListFilterKeyRef.current = listFilterKey;
    setPage(1);
  }, [listFilterKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userData = await WhatsAppAPI.getLoginUserData();
        if (!cancelled) setUser(userData);
        const fetchUser = await WhatsAppAPI.fetchUsers();
        if (cancelled) return;
        const formattedAgents = fetchUser.map((item) => ({
          value: item.id,
          label: item.username,
          email: item.email || "",
          full_name: item.full_name || "",
          ...item,
        }));
        setAvailableAgents(formattedAgents);

        const tagRes = await WhatsAppAPI.fetchLeadTagOptions();
        if (
          !cancelled &&
          tagRes &&
          tagRes.success &&
          Array.isArray(tagRes.records)
        ) {
          const rows = tagRes.records;
          setTagMasterList(
            rows.map((r) => ({
              id: r.id,
              name: r.name,
              color: r.color || "#6c757d",
            })),
          );
          setAvailableTags(
            rows.map((r) => ({
              value: r.id,
              label: r.name,
              color: r.color || "#6c757d",
            })),
          );
        }
      } catch (e) {
        console.error("Lead list bootstrap:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAgents = async () => {
    try {
      const fetchUser = await WhatsAppAPI.fetchUsers();
      const formattedAgents = fetchUser.map((item) => ({
        value: item.id,
        label: item.username,
        email: item.email || "",
        full_name: item.full_name || "",
        ...item,
      }));
      setAvailableAgents(formattedAgents);
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to load agents");
    }
  };

  useEffect(() => {
    setSelectedLeads(
      selectedRows.map((id) => leadCacheRef.current.get(id)).filter(Boolean),
    );
  }, [selectedRows, body]);

  const onFilterType = (event) => {
    setStatusFilter(event.target.value);
  };

  const getStatusClass = (status) => {
    let accessStatusRec = leadStatusArray.filter((value, index, array) => {
      if (value.label === status) {
        return true;
      }
    });

    if (accessStatusRec && accessStatusRec.length > 0) {
      if (accessStatusRec[0].is_converted === true) {
        return "success";
      } else if (accessStatusRec[0].is_lost === true) {
        return "secondary";
      } else {
        return "primary";
      }
    } else {
      return "secondary";
    }
  };

  const editLead = (lead) => {
    navigate(`/leads/${lead.id}/e`, { state: lead });
  };

  const handlePinToggle = async (lead) => {
    if (!user || pinLoading[lead.id]) return;
    setPinLoading((prev) => ({ ...prev, [lead.id]: true }));
    const previousPinned = !!lead.pinned;
    const nextPinned = !previousPinned;
    const cacheKey = buildListCacheKey();

    setBody((prev) =>
      prev.map((row) =>
        row.id === lead.id ? { ...row, pinned: nextPinned } : row,
      ),
    );
    const cached = pageListCacheRef.current.get(cacheKey);
    if (cached) {
      pageListCacheRef.current.set(cacheKey, {
        ...cached,
        records: cached.records.map((r) =>
          r.id === lead.id ? { ...r, pinned: nextPinned } : r,
        ),
      });
    }
    leadCacheRef.current.set(lead.id, { ...lead, pinned: nextPinned });

    try {
      if (previousPinned) {
        await WhatsAppAPI.unpinLead(lead.id);
      } else {
        await WhatsAppAPI.pinLead(lead.id);
      }
    } catch (error) {
      console.error("Pin toggle failed:", error);
      setBody((prev) =>
        prev.map((row) =>
          row.id === lead.id ? { ...row, pinned: previousPinned } : row,
        ),
      );
      const c2 = pageListCacheRef.current.get(cacheKey);
      if (c2) {
        pageListCacheRef.current.set(cacheKey, {
          ...c2,
          records: c2.records.map((r) =>
            r.id === lead.id ? { ...r, pinned: previousPinned } : r,
          ),
        });
      }
      leadCacheRef.current.set(lead.id, { ...lead, pinned: previousPinned });
      toast.error("Could not update pin. Please try again.");
    } finally {
      setPinLoading((prev) => ({ ...prev, [lead.id]: false }));
    }
  };

  const handleAssignAgentsClick = async () => {
    await fetchAgents();
    setShowAssignAgentsModal(true);
  };

  const handleBulkTagsClick = () => {
    setTagsForBulkApply([]);
    setTagBulkMode("add");
    setShowBulkTagsModal(true);
  };

  const handleCloseBulkTagsModal = () => {
    setShowBulkTagsModal(false);
    setTagsForBulkApply([]);
    setTagBulkMode("add");
  };

  const handleBulkTagsApply = async () => {
    if (selectedRows.length === 0) {
      toast.error("No leads selected");
      return;
    }
    if (!tagsForBulkApply || tagsForBulkApply.length === 0) {
      toast.error("Select at least one tag");
      return;
    }

    setBulkTagsLoading(true);
    try {
      const tags = tagsForBulkApply.map((t) => ({ id: t.value }));
      const response = await WhatsAppAPI.addTagsToLeads({
        leadIds: selectedRows,
        tags,
        tagMode: tagBulkMode,
      });
      if (response.success) {
        toast.success(
          response.message || `Tags updated on ${selectedRows.length} lead(s)`,
        );
        await loadLeadsFromServer({ forceRefresh: true });
        setSelectedRows([]);
        setSelectedLeads([]);
        setTagsForBulkApply([]);
        setShowBulkTagsModal(false);
      } else {
        toast.error(response.message || "Failed to update tags");
      }
    } catch (error) {
      console.error("Bulk tags error:", error);
      toast.error("Failed to update tags");
    } finally {
      setBulkTagsLoading(false);
    }
  };

  const handleAssignAgents = async () => {
    if (selectedAgents.length === 0) {
      toast.error("Please select at least one agent");
      return;
    }

    if (selectedRows.length === 0) {
      toast.error("No leads selected");
      return;
    }

    setAssignLoading(true);
    try {
      const agentIds = selectedAgents.map((agent) => agent.value);
      const leadIds = selectedRows;

      // Prepare data for API call
      const assignData = {
        leadIds: leadIds,
        agentIds: agentIds,
        assignMode: assignMode, // 'replace' or 'add'
      };

      // console.log("assi", assignData);
      // Call API to assign agents to selected leads
      const response = await WhatsAppAPI.assignAgentsToLeads(assignData);

      if (response.success) {
        toast.success(
          `Successfully assigned agents to ${leadIds.length} lead(s)`,
        );
        // Refresh the data
        await loadLeadsFromServer({ forceRefresh: true });
        // Clear selections
        setSelectedRows([]);
        setSelectedLeads([]);
        setSelectedAgents([]);
        // Close modal
        setShowAssignAgentsModal(false);
      } else {
        toast.error(response.message || "Failed to assign agents");
      }
    } catch (error) {
      console.error("Error assigning agents:", error);
      toast.error("Failed to assign agents");
    } finally {
      setAssignLoading(false);
    }
  };

  const removeSelectedLead = (leadId) => {
    setSelectedRows((prev) => prev.filter((id) => id !== leadId));
  };

  const handleCloseAssignModal = () => {
    setShowAssignAgentsModal(false);
    setSelectedAgents([]);
    setAssignMode("replace");
  };

  const clearAllSelections = () => {
    setSelectedRows([]);
    setSelectedLeads([]);
  };

  const header = useMemo(() => {
    const cols = [];
    if (!isMobile) {
      cols.push(
        {
          title: <SelectAllCheckbox />,
          cell: (row) => (
            <Form.Check
              type="checkbox"
              id={`row-checkbox-${row.id}`}
              checked={selectedRowsRef.current.includes(row.id)}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedRows((prev) =>
                  checked
                    ? [...prev, row.id]
                    : prev.filter((id) => id !== row.id),
                );
              }}
            />
          ),
          prop: "select",
        },

        {
          title: "Pin",
          prop: "pin",
          cell: (row) => {
            const isOwner = user && String(row.createdbyid) === String(user.id);
            return (
              <Button
                variant="link"
                className="p-0 m-0"
                style={{
                  color: row.pinned
                    ? "var(--theme-color-2)"
                    : "var(--text-color-3)",
                }}
                onClick={() => isOwner && handlePinToggle(row)}
                disabled={!isOwner || pinLoading[row.id]}
                title={row.pinned ? "Unpin" : "Pin"}
              >
                <FaThumbtack style={row.pinned ? {} : { opacity: 0.5 }} />
              </Button>
            );
          },
        },
        {
          title: "S.No",
          prop: "lead_no",
          isFilterable: false,
        },
        {
          title: "Name",
          prop: "leadname",
          isFilterable: false,
          cell: (row) => (
            <Link
              className="text-capitalize"
              to={"/leads/" + row.id}
              state={row}
            >
              {row.leadname}
            </Link>
          ),
        },
        {
          title: "WhatsApp",
          prop: "whatsapp_number",
          isFilterable: false,
          cell: (row) => (
            <span>{`${row.country_code} ${row.display_whatsapp_number ||
              formatPhoneNumber(row.whatsapp_number)
              }`}</span>
          ),
        },
        {
          title: "Status",
          prop: "leadstatus",
          isFilterable: false,
          cell: (row) => {
            const status = row.leadstatus || "—";
            const lower = String(status).toLowerCase();
            if (lower.includes("lost") || lower.includes("aborted")) {
              return (
                <Badge bg="danger" className="px-2 py-1 fw-semibold">
                  {status}
                </Badge>
              );
            }
            return (
              <Badge bg="light" className="badge-assign-muted text-capitalize">
                {status}
              </Badge>
            );
          },
        },
        {
          title: "Assigned To",
          prop: "all_owner_names",
          cell: (row) => {
            const owners = row.all_owner_names || [];
            const visibleOwners = owners.slice(0, 2);
            const remainingCount = owners.length - 2;

            return (
              <div
                style={{
                  maxWidth: "320px",
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {visibleOwners.map((owner, index) => (
                  <span key={index} className="badge-assign">
                    {owner.name} <FaUsers size={12} className="ms-1" />
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span className="badge-assign-muted">
                    +{remainingCount} more
                  </span>
                )}
              </div>
            );
          },
        },
        {
          title: "Tags",
          prop: "tag_names",
          cell: (row) => {
            const tags = row.tag_names || [];
            const visibleTags = tags.slice(0, 2);
            const remainingCount = tags.length - 2;

            return (
              <div
                style={{
                  maxWidth: "320px",
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {visibleTags.map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-light text-dark border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: tag.color || "#f0f0f0",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {tag.name} <FaTags size={14} className="ms-2" />
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span
                    className="badge bg-light text-dark border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ccc",
                    }}
                  >
                    +{remainingCount} more
                  </span>
                )}
              </div>
            );
          },
        },

        {
          title: "Created At",
          prop: "createddate",
          isFilterable: false,
          cell: (row) => formatDate(row.createddate),
        },
        {
          title: "Actions",
          prop: "actions",
          cell: (row) =>
            !(
              userInfo?.userrole !== "ADMIN" &&
              userInfo?.is_whatsapp_number_hidden === true
            ) && (
              <Button
                variant="light"
                className="btn-action-icon mx-2"
                onClick={() => editLead(row)}
                title="Edit"
              >
                <i className="fa-regular fa-pen-to-square"></i>
              </Button>
            ),
        },
      );
    } else {
      cols.push({
        title: "Info",
        prop: "leadname",
        isFilterable: false,
        cell: (row) => (
          <div className="mobilecard">
            <Link
              to={"/leads/" + row.id}
              state={row}
              style={{ fontSize: "1.2rem" }}
            >
              {row.leadname}
            </Link>
            <div className="mb-2">
              <i className="fa-solid fa-users me-2"></i>
              {row.all_owner_names && row.all_owner_names.length > 0 ? (
                row.all_owner_names.map((owner, idx) => (
                  <Badge key={idx} className="badge-assign me-1 mb-1">
                    {owner.name}
                  </Badge>
                ))
              ) : (
                <span className="text-muted">No owners assigned</span>
              )}
            </div>

            <span>
              <i className="fa-solid fa-phone"></i>{" "}
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
            <span style={{ width: "80%" }}>
              <i className="fa-solid fa-envelope"></i> {row.email}
            </span>
            <Badge
              bg={getStatusClass(row.leadstatus)}
              style={{ paddingBottom: "5px", width: "80%" }}
            >
              {row.leadstatus}
            </Badge>
            {!(
              userInfo?.userrole !== "ADMIN" &&
              userInfo?.is_whatsapp_number_hidden === true
            ) && (
                <Button
                  variant="light"
                  className="btn-action-icon mx-2"
                  onClick={() => editLead(row)}
                  title="Edit"
                >
                  <i className="fa-regular fa-pen-to-square"></i>
                </Button>
              )}
          </div>
        ),
      });
    }

    return cols;
    // user in deps so pin column shows after refresh when user loads; checkbox cells use refs for stability.
  }, [user]);

  const createLead = () => {
    navigate(`/leads/e`);
  };

  const handleImportLeads = () => {
    setShowModal(true);
  };

  const refreshData = () => {
    setShowModal(false);
    loadLeadsFromServer({ forceRefresh: true });
  };

  const applyFilters = useCallback(() => {
    const fromT = (createdFromDate || "").trim();
    const toT = (createdToDate || "").trim();
    if (fromT && toT && fromT > toT) {
      toast.warning(
        'Created "From" date must be on or before "To" date.',
      );
      return;
    }
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedStatusFilter(statusFilter || "");
    setAppliedSelectedTags([...selectedTags]);
    setAppliedSelectedAgentFilter([...selectedAgentFilter]);
    setAppliedCreatedFromDate(fromT);
    setAppliedCreatedToDate(toT);
  }, [
    searchQuery,
    statusFilter,
    selectedTags,
    selectedAgentFilter,
    createdFromDate,
    createdToDate,
  ]);

  const handleDownloadFilteredLeads = useCallback(async () => {
    if (downloadLeadsInFlightRef.current) return;
    const fromD = (createdFromDate || "").trim();
    const toD = (createdToDate || "").trim();
    if (fromD && toD && fromD > toD) {
      toast.warning(
        'Created "From" date must be on or before "To" date.',
      );
      return;
    }
    downloadLeadsInFlightRef.current = true;
    setDownloadLeadsLoading(true);
    const EXPORT_PAGE_SIZE = 100;
    // Use current filter controls (same as list after Apply). If you pick e.g. tag "10 march"
    // and download without clicking Apply, we still request with that tag so the CSV matches
    // your selected filters, not the previous applied set.
    const listParams = {
      q: searchQuery.trim() || undefined,
      status: statusFilter || undefined,
      business_number: selectedWhatsAppSetting || undefined,
      tags:
        selectedTags.length > 0
          ? selectedTags.map((t) => t.value).join(",")
          : undefined,
      agents:
        selectedAgentFilter.length > 0
          ? selectedAgentFilter.map((a) => String(a.value)).join(",")
          : undefined,
      createdFrom: fromD || undefined,
      createdTo: toD || undefined,
    };
    const csvCell = (val) =>
      `"${String(val ?? "").replace(/"/g, '""')}"`;
    const tagStr = (row) => {
      let tags = row.tag_names;
      if (tags == null) return "";
      if (typeof tags === "string") {
        try {
          tags = JSON.parse(tags);
        } catch {
          return tags;
        }
      }
      if (!Array.isArray(tags)) return "";
      return tags
        .map((t) => (t && typeof t === "object" ? t.name : t))
        .filter(Boolean)
        .join("; ");
    };
    try {
      let pageNum = 1;
      let total = 0;
      const allRows = [];
      while (true) {
        const leads = await WhatsAppAPI.fetchLead({
          ...listParams,
          page: pageNum,
          pageSize: EXPORT_PAGE_SIZE,
        });
        if (!leads || !leads.success) {
          toast.error("Failed to download leads. Please try again.");
          return;
        }
        const rows = leads.records || [];
        total = typeof leads.total === "number" ? leads.total : rows.length;
        allRows.push(...rows);
        if (allRows.length >= total || rows.length === 0) break;
        pageNum += 1;
      }
      if (allRows.length === 0) {
        toast.info("No leads match the current filters.");
        return;
      }
      const header = [
        "Name",
        "WhatsApp",
        "Email",
        "Status",
        "Assigned To",
        "Tags",
        "Created Date",
      ];
      const csvRows = allRows.map((row) => {
        const wa =
          `${row.country_code || ""} ${row.display_whatsapp_number ||
            formatPhoneNumber(row.whatsapp_number)
            }`.trim();
        const owners = (row.all_owner_names || [])
          .map((o) => o.name)
          .filter(Boolean)
          .join("; ");
        const created = row.createddate
          ? moment(row.createddate).format("DD-MM-YYYY")
          : "";
        return [
          csvCell(row.leadname || ""),
          csvCell(wa),
          csvCell(row.email || ""),
          csvCell(row.leadstatus || ""),
          csvCell(owners),
          csvCell(tagStr(row)),
          csvCell(created),
        ].join(",");
      });
      const csvContent = [header.join(","), ...csvRows].join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        `leads_${moment().format("YYYYMMDD_HHmm")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${allRows.length} lead(s).`);
    } catch (e) {
      console.error("Download leads:", e);
      toast.error("Could not download leads.");
    } finally {
      downloadLeadsInFlightRef.current = false;
      setDownloadLeadsLoading(false);
    }
  }, [
    searchQuery,
    statusFilter,
    selectedTags,
    selectedAgentFilter,
    createdFromDate,
    createdToDate,
    userInfo,
  ]);

  // Function to clear all filters (draft + applied) and refetch via listFilterKey change
  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setSelectedTags([]);
    setSelectedAgentFilter([]);
    setCreatedFromDate("");
    setCreatedToDate("");
    setAppliedSearchQuery("");
    setAppliedStatusFilter("");
    setAppliedSelectedTags([]);
    setAppliedSelectedAgentFilter([]);
    setAppliedCreatedFromDate("");
    setAppliedCreatedToDate("");
  };

  const noActiveOrDraftFilters = useMemo(
    () =>
      !String(searchQuery || "").trim() &&
      !statusFilter &&
      selectedTags.length === 0 &&
      selectedAgentFilter.length === 0 &&
      !(createdFromDate || "").trim() &&
      !(createdToDate || "").trim() &&
      !String(appliedSearchQuery || "").trim() &&
      !appliedStatusFilter &&
      appliedSelectedTags.length === 0 &&
      appliedSelectedAgentFilter.length === 0 &&
      !(appliedCreatedFromDate || "").trim() &&
      !(appliedCreatedToDate || "").trim(),
    [
      searchQuery,
      statusFilter,
      selectedTags,
      selectedAgentFilter,
      createdFromDate,
      createdToDate,
      appliedSearchQuery,
      appliedStatusFilter,
      appliedSelectedTags,
      appliedSelectedAgentFilter,
      appliedCreatedFromDate,
      appliedCreatedToDate,
    ],
  );

  // Function to remove a specific tag from selection
  const removeTag = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((tag) => tag.value !== tagToRemove));
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      minHeight: "38px",
    }),
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: state.data.color || "#e9ecef",
      borderRadius: "4px",
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.data.color ? "#fff" : "#212529",
      fontWeight: "500",
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      color: state.data.color ? "#fff" : "#6c757d",
      "&:hover": {
        backgroundColor: state.data.color ? "rgba(0,0,0,0.2)" : "#dee2e6",
        color: state.data.color ? "#fff" : "#dc3545",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  // Custom filter function for agents that searches across multiple fields
  const filterAgentOptions = (option, inputValue) => {
    const searchTerm = inputValue.toLowerCase().trim();
    if (!searchTerm) return true;

    const agent = option.data;
    return (
      agent.label?.toLowerCase().includes(searchTerm) ||
      agent.email?.toLowerCase().includes(searchTerm) ||
      agent.full_name?.toLowerCase().includes(searchTerm) ||
      agent.username?.toLowerCase().includes(searchTerm) ||
      false
    );
  };

  // Format agent labels with additional info
  const formatAgentLabel = (agent, { context }) => {
    if (context === "value") {
      return agent.label;
    }
    return (
      <div>
        <div className="fw-semibold">{agent.label}</div>
        {agent.email && <div className="small text-muted">{agent.email}</div>}
        {agent.full_name && agent.full_name !== agent.label && (
          <div className="small text-muted">{agent.full_name}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Container className="mt-5 mb-5">
        {/* Quick Guide Card */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Manage your leads efficiently. Add tags to categorize leads and
              track their status. Use the search and filter options below to
              find leads quickly.
            </small>
          </Card>
        </Row>

        {/* Search & Filter Card */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <Row className="g-3 align-items-end">
              {/* Search Input */}
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaSearch size={12} className="me-1" /> Search Leads
                  </Form.Label>
                  <Form.Control
                    type="text"
                    style={{ height: "36px" }}
                    placeholder="Search by name, email, phone, status, or agent..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (filtersDirty) applyFilters();
                      }
                    }}
                    className="border-primary"
                  />
                </Form.Group>
              </Col>

              {/* Status Filter */}
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaFilter size={12} className="me-1" /> Status
                  </Form.Label>
                  <Form.Select
                    aria-label="Filter by status"
                    value={statusFilter}
                    onChange={onFilterType}
                    style={{ height: "36px" }}
                    className="border-primary"
                  >
                    <option value="">All Status</option>
                    {leadStatusArray.map((item, index) => (
                      <option value={item.label} key={index}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Tags Filter */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaTag size={12} className="me-1" /> Filter by Tags
                  </Form.Label>
                  <Select
                    isMulti
                    options={availableTags}
                    value={selectedTags}
                    onChange={setSelectedTags}
                    placeholder="Select tags"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    isSearchable
                    noOptionsMessage={() => "No tags found"}
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Form.Group>
              </Col>

              {/* Agent Filter */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaUsers size={12} className="me-1" /> Filter by Agents
                  </Form.Label>
                  <Select
                    isMulti
                    options={availableAgents}
                    value={selectedAgentFilter}
                    onChange={setSelectedAgentFilter}
                    placeholder="Select agents"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    isSearchable
                    noOptionsMessage={() => "No agents found"}
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    filterOption={filterAgentOptions}
                    formatOptionLabel={formatAgentLabel}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 mt-2 align-items-end">
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="small text-muted mb-1">
                    Created From
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={createdFromDate}
                    max={createdToDate || undefined}
                    onChange={(e) => setCreatedFromDate(e.target.value)}
                    style={{ height: "36px" }}
                    className="border-primary"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="small text-muted mb-1">
                    Created To
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={createdToDate}
                    min={createdFromDate || undefined}
                    onChange={(e) => setCreatedToDate(e.target.value)}
                    style={{ height: "36px" }}
                    className="border-primary"
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex justify-content-end gap-2 flex-wrap">
                <Button
                  variant="primary"
                  onClick={applyFilters}
                  disabled={!filtersDirty}
                  className="py-2"
                  title="Apply the filters above to the lead list"
                >
                  Apply filters
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={handleDownloadFilteredLeads}
                  disabled={downloadLeadsLoading}
                  className="py-2"
                  title="Download CSV for leads matching the filters above"
                >
                  {downloadLeadsLoading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-1 align-middle"
                      />
                      Preparing…
                    </>
                  ) : (
                    <>
                      <FaDownload className="me-1" />
                      Download
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={clearAllFilters}
                  className="py-2"
                  title="Clear all filters and reload the full list"
                  disabled={noActiveOrDraftFilters}
                >
                  <FaTimes className="me-1" />
                  Clear all
                </Button>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <small className="text-muted">
                  <strong>Filters only affect the list after you click</strong>{" "}
                  <strong>&quot;Apply filters&quot;</strong> (or press Enter in
                  the search box).
                  <br />
                  <strong>Search includes:</strong> Name, Email, Phone, Status,
                  Agent names, Agent emails |<strong> Tag filter:</strong> OR
                  logic (shows leads with ANY selected tag) |
                  <strong> Agent filter:</strong> OR logic (shows leads assigned
                  to ANY selected agent) |<strong> Created date:</strong> if you
                  set only <strong>From</strong> or only <strong>To</strong>,
                  it matches that exact day. If you set both, it matches the
                  inclusive range. Leave both empty to disable date filtering.
                </small>
              </Col>
            </Row>
          </Card>
        </Row>

        {/* Leads Table Card */}
        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 px-4 pb-4 pt-3">
                  <h5 className="fw-semibold mb-0 mt-0 lh-base">
                    👥 All Leads
                  </h5>

                  {isSpinner ? (
                    <LeadListSelectionContext.Provider
                      value={selectionContextValue}
                    >
                      <div
                        className="position-relative mt-0 pt-0"
                        style={{ transition: "opacity 0.2s ease" }}
                      >
                        {listBusy && (
                          <div
                            className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center rounded-3"
                            style={{
                              zIndex: 3,
                              background: "rgba(255,255,255,0.65)",
                            }}
                            aria-busy="true"
                            aria-live="polite"
                          >
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="text-primary"
                            />
                          </div>
                        )}
                        <DatatableWrapper
                          isControlled
                          body={body}
                          keyField="id"
                          headers={header}
                          paginationOptionsProps={{
                            initialState: {
                              rowsPerPage: pageSize,
                              options: [5, 10, 15, 20, 25, 50],
                            },
                          }}
                        >
                          <div className="d-flex flex-nowrap align-items-center justify-content-between gap-2 gap-md-3 mb-2 w-100 min-w-0 overflow-x-auto pb-1">
                            <div className="d-flex flex-nowrap align-items-center gap-2 gap-md-3 flex-shrink-0 min-w-0">
                              <span className="small text-muted text-nowrap py-0">
                                Search and filters are above.
                              </span>
                              <PaginationOptions
                                labels={paginationLabels}
                                classes={{
                                  formGroup: "mb-0",
                                  formText: "mb-0 small",
                                }}
                                controlledProps={{
                                  rowsPerPage: pageSize,
                                  rowsPerPageOptions: [5, 10, 15, 20, 25, 50],
                                  filteredDataLength: totalLeads,
                                  onRowsPerPageChange: (n) => {
                                    setPageSize(n);
                                    setPage(1);
                                  },
                                }}
                              />
                            </div>
                            <div className="d-flex flex-nowrap align-items-center gap-2 flex-shrink-0">
                              {userInfo?.userrole !== "USER" && (
                                <Button
                                  className="btn-sm text-nowrap"
                                  variant="outline-success"
                                  onClick={handleAssignAgentsClick}
                                  disabled={
                                    selectedRows.length === 0 || loading
                                  }
                                  title="Assign agents to selected leads"
                                >
                                  <FaUserPlus size={12} className="me-1" />
                                  Assign Agents ({selectedRows.length})
                                </Button>
                              )}

                              <Button
                                className="btn-sm text-nowrap"
                                variant="outline-info"
                                onClick={handleBulkTagsClick}
                                disabled={selectedRows.length === 0 || loading}
                                title="Add or replace tags on selected leads"
                              >
                                <FaTags size={12} className="me-1" />
                                Tags ({selectedRows.length})
                              </Button>

                              <Button
                                className="btn-sm text-nowrap"
                                variant="outline-primary"
                                onClick={handleImportLeads}
                              >
                                <FaUpload size={12} className="me-1" /> Import
                                Leads
                              </Button>
                              <Button
                                className="btn-sm text-nowrap"
                                variant="primary"
                                onClick={() => createLead(true)}
                              >
                                <FaPlus size={12} className="me-1" /> Add New
                                Lead
                              </Button>
                              <Button
                                className="btn-sm text-nowrap"
                                variant="outline-danger"
                                disabled={selectedRows.length === 0 || loading}
                                onClick={async () => {
                                  setShowConfirmDelete(true);
                                }}
                              >
                                Delete Selected ({selectedRows.length})
                              </Button>
                              <Button
                                className="btn-sm text-nowrap"
                                variant="outline-warning"
                                as={Link}
                                to="/leads/blocked"
                              >
                                <FaBan size={12} className="me-1" /> OTP Out /
                                Blocked
                              </Button>
                            </div>
                          </div>

                          <div
                            className="table-responsive"
                            style={{
                              opacity: listBusy ? 0.55 : 1,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            <Table
                              striped
                              bordered
                              className="related-list-table"
                              responsive="sm"
                            >
                              <TableHeader />
                              <TableBody />
                            </Table>
                          </div>
                          <Pagination
                            controlledProps={{
                              currentPage: page,
                              maxPage: totalPages,
                              onPaginationChange: (p) => setPage(p),
                            }}
                          />
                        </DatatableWrapper>
                      </div>
                    </LeadListSelectionContext.Provider>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Assign Agents Modal */}
      <Modal
        show={showAssignAgentsModal}
        onHide={handleCloseAssignModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaUsers className="me-2" />
            Assign Agents to Leads
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-4"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* Selected Leads Section with fixed height and scroll */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              <FaUserFriends className="me-2" />
              Selected Leads ({selectedRows.length})
            </h6>
            <Card className="border">
              <Card.Body
                className="p-3"
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  minHeight: "100px",
                }}
              >
                {selectedLeads.length > 0 ? (
                  <ListGroup variant="flush">
                    {selectedLeads.map((lead) => (
                      <ListGroup.Item
                        key={lead.id}
                        className="d-flex justify-content-between align-items-start"
                        style={{ borderLeft: "none", borderRight: "none" }}
                      >
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="d-block">
                                {lead.leadname}
                              </strong>
                              <div className="small text-muted">
                                {lead.email} • {lead.whatsapp_number}
                              </div>
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0 ms-2"
                              onClick={() => removeSelectedLead(lead.id)}
                              title="Remove from selection"
                            >
                              <FaXmark size={16} />
                            </Button>
                          </div>
                          <Badge
                            bg={getStatusClass(lead.leadstatus)}
                            className="me-2 mt-2"
                          >
                            {lead.leadstatus}
                          </Badge>
                          {/* Show all current owners */}
                          {lead.all_owner_names &&
                            lead.all_owner_names.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted d-block mb-1">
                                  Current Owners:
                                </small>
                                <div className="d-flex flex-wrap gap-1">
                                  {lead.all_owner_names.map((owner, idx) => (
                                    <Badge
                                      key={idx}
                                      className="badge-assign me-1 mb-1"
                                    >
                                      {owner.name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">
                    <FaUserFriends size={24} className="mb-2" />
                    <div>No leads selected</div>
                    <small>Select leads from the table first</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Assign Mode Selection */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">Assignment Mode</h6>
            <Form>
              <Form.Check
                type="radio"
                id="replace-mode"
                label={
                  <>
                    <FaUserMinus className="me-2 text-danger" />
                    <strong>Replace Existing Agents</strong> - Remove all
                    current agents and assign only the selected agents
                  </>
                }
                name="assignMode"
                value="replace"
                checked={assignMode === "replace"}
                onChange={(e) => setAssignMode(e.target.value)}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="add-mode"
                label={
                  <>
                    <FaUserPlus className="me-2 text-success" />
                    <strong>Add More Agents</strong> - Keep current agents and
                    add selected agents to them
                  </>
                }
                name="assignMode"
                value="add"
                checked={assignMode === "add"}
                onChange={(e) => setAssignMode(e.target.value)}
              />
            </Form>
          </div>

          {/* Agents Selection */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              <FaUserCheck className="me-2" />
              Select Agents
            </h6>
            <Form.Group>
              <Select
                isMulti
                options={availableAgents}
                value={selectedAgents}
                onChange={setSelectedAgents}
                placeholder="Search and select agents (search by name, email, or username)..."
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customSelectStyles}
                isSearchable
                noOptionsMessage={() => "No agents found"}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                filterOption={filterAgentOptions}
                formatOptionLabel={formatAgentLabel}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Form.Text className="text-muted">
                {assignMode === "replace"
                  ? "Selected agents will replace all current agents"
                  : "Selected agents will be added to existing agents"}
              </Form.Text>
            </Form.Group>
          </div>

          {/* Summary */}
          <Card className="border-info bg-info bg-opacity-10">
            <Card.Body className="p-3">
              <h6 className="fw-semibold">Summary</h6>
              <div className="small">
                <div>
                  • Leads to update: <strong>{selectedRows.length}</strong>
                </div>
                <div>
                  • Agents to assign: <strong>{selectedAgents.length}</strong>
                </div>
                <div>
                  • Mode:{" "}
                  <strong>
                    {assignMode === "replace" ? "Replace" : "Add"}
                  </strong>
                </div>
                <div>
                  • Agent names:{" "}
                  {selectedAgents.map((a) => a.label).join(", ") ||
                    "None selected"}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={clearAllSelections}
            disabled={selectedRows.length === 0}
          >
            Clear Selections
          </Button>
          <Button variant="secondary" onClick={handleCloseAssignModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignAgents}
            disabled={selectedAgents.length === 0 || assignLoading}
          >
            {assignLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Assigning...
              </>
            ) : (
              `Assign ${selectedAgents.length} Agent(s) to ${selectedRows.length} Lead(s)`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk tags — same selection as Assign Agents */}
      <Modal
        show={showBulkTagsModal}
        onHide={handleCloseBulkTagsModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaTags className="me-2" />
            Tags on selected leads
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-4"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              <FaUserFriends className="me-2" />
              Selected leads ({selectedRows.length})
            </h6>
            <Card className="border">
              <Card.Body
                className="p-3"
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  minHeight: "100px",
                }}
              >
                {selectedLeads.length > 0 ? (
                  <ListGroup variant="flush">
                    {selectedLeads.map((lead) => (
                      <ListGroup.Item
                        key={lead.id}
                        className="d-flex justify-content-between align-items-start"
                        style={{ borderLeft: "none", borderRight: "none" }}
                      >
                        <div>
                          <strong className="d-block">{lead.leadname}</strong>
                          <div className="small text-muted">
                            {lead.email} • {lead.whatsapp_number}
                          </div>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0 ms-2"
                          onClick={() => removeSelectedLead(lead.id)}
                          title="Remove from selection"
                        >
                          <FaXmark size={16} />
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">
                    <FaUserFriends size={24} className="mb-2" />
                    <div>No leads selected</div>
                    <small>Select rows using the checkboxes first</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          <div className="mb-4">
            <h6 className="fw-semibold mb-2">Tag mode</h6>
            <Form>
              <Form.Check
                type="radio"
                id="tag-mode-add"
                label={
                  <>
                    <FaTag className="me-2 text-success" />
                    <strong>Add tags</strong> — keep existing tags and add the
                    selected ones
                  </>
                }
                name="tagBulkMode"
                value="add"
                checked={tagBulkMode === "add"}
                onChange={(e) => setTagBulkMode(e.target.value)}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="tag-mode-replace"
                label={
                  <>
                    <FaTimes className="me-2 text-danger" />
                    <strong>Replace tags</strong> — remove current tags and set
                    only the tags you select below
                  </>
                }
                name="tagBulkMode"
                value="replace"
                checked={tagBulkMode === "replace"}
                onChange={(e) => setTagBulkMode(e.target.value)}
              />
            </Form>
          </div>

          <div className="mb-2">
            <h6 className="fw-semibold mb-2">
              <FaTags className="me-2" />
              Tags
            </h6>
            <Form.Group>
              <Select
                isMulti
                options={tagMasterList.map((r) => ({
                  value: r.id,
                  label: r.name,
                }))}
                value={tagsForBulkApply}
                onChange={setTagsForBulkApply}
                placeholder="Search and select tags..."
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customSelectStyles}
                isSearchable
                noOptionsMessage={() => "No tags — create tags under Tags menu"}
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Form.Text className="text-muted">
                {tagBulkMode === "replace"
                  ? "These tags will be the only tags on each selected lead."
                  : "These tags will be merged with each lead’s current tags (duplicates skipped)."}
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseBulkTagsModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkTagsApply}
            disabled={
              bulkTagsLoading ||
              selectedRows.length === 0 ||
              tagsForBulkApply.length === 0
            }
          >
            {bulkTagsLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Updating…
              </>
            ) : (
              `Apply to ${selectedRows.length} lead(s)`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modals */}
      {showModal && (
        <ImportLeads
          show={showModal}
          onHide={() => setShowModal(false)}
          refreshData={refreshData}
        />
      )}

      {showConfirmDelete && (
        <Confirm
          show={showConfirmDelete}
          onHide={() => setShowConfirmDelete(false)}
          deleteLead={async () => {
            setLoading(true);
            try {
              await WhatsAppAPI.bulkDeleteLeads({ ids: selectedRows });
              loadLeadsFromServer({ forceRefresh: true });
              setData((prev) =>
                prev.filter((row) => !selectedRows.includes(row.id)),
              );
              setSelectedRows([]);
              setSelectedLeads([]);
              toast.success("Deleted successfully!");
            } catch (err) {
              console.error(err);
              toast.error("Failed to delete");
            } finally {
              setLoading(false);
              setShowConfirmDelete(false);
            }
          }}
          title="Confirm delete?"
          message={`You are going to delete ${selectedRows.length} record(s). This will also permanently delete all associated data, history, and media files from both the database and folders. Are you sure?`}
          table="lead"
        />
      )}
      <ToastContainer />
    </>
  );
};

export default LeadList;
